import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useBranding } from '../BrandingContext';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuShieldCheck } from 'react-icons/lu'; // Иконка логотипа

// --- Компонент Входа ---
const LoginForm = ({ onLogin }) => {
  const { t } = useTranslation(); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, telegramLogin } = useAuth();
  const [notVerifiedError, setNotVerifiedError] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setNotVerifiedError(false); setResendStatus(null); setLoading(true);
    try {
      const { role } = await login(email, password);
      onLogin(role); 
    } catch (err) {
      if (err.message === "NOT_VERIFIED") {
        setNotVerifiedError(true); setError(t('auth.error_not_verified'));
      } else { setError(err.message || t('auth.error_login')); }
      setLoading(false);
    }
  };
  
  const handleResendVerification = async () => {
    setResendStatus('sending'); setError(null);
    try {
      const response = await fetch('/api/public/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setResendStatus('success');
    } catch (e) { setError(e.message); setResendStatus(null); }
  };

  const handleForgotPassword = async (e) => {
    if (e) {
    e.preventDefault();
    e.stopPropagation();
    }
    console.log('[FORGOT PASSWORD] ========== НАЧАЛО ОБРАБОТКИ ==========');
    console.log('[FORGOT PASSWORD] Email:', forgotPasswordEmail);
    
    if (!forgotPasswordEmail || !forgotPasswordEmail.trim()) {
      setError('Пожалуйста, введите email');
      return;
    }
    
    setError(null);
    setForgotPasswordSuccess(false);
    setForgotPasswordLoading(true);
    
    try {
      const url = '/api/public/forgot-password';
      const requestBody = { email: forgotPasswordEmail.trim().toLowerCase() };
      console.log('[FORGOT PASSWORD] Отправка запроса на:', url);
      console.log('[FORGOT PASSWORD] Body:', requestBody);
      console.log('[FORGOT PASSWORD] Full URL будет:', window.location.origin + url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
        credentials: 'same-origin'
      });
      
      console.log('[FORGOT PASSWORD] Ответ получен, status:', response.status);
      console.log('[FORGOT PASSWORD] Response ok:', response.ok);
      console.log('[FORGOT PASSWORD] Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Проверяем тип контента
      const contentType = response.headers.get('content-type');
      console.log('[FORGOT PASSWORD] Content-Type:', contentType);
      
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        console.log('[FORGOT PASSWORD] Данные ответа (JSON):', data);
      } else {
        const text = await response.text();
        console.log('[FORGOT PASSWORD] Данные ответа (текст):', text);
        try {
          data = JSON.parse(text);
        } catch {
          data = { message: text || 'Unknown response' };
        }
      }
      
      if (!response.ok) {
        console.error('[FORGOT PASSWORD] Ошибка ответа, status:', response.status);
        console.error('[FORGOT PASSWORD] Данные ошибки:', data);
        throw new Error(data.message || `Ошибка сервера: ${response.status}`);
      }
      
      console.log('[FORGOT PASSWORD] Успешный ответ:', data);
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail('');
      console.log('[FORGOT PASSWORD] Успешно, показываем сообщение об успехе');
    } catch (e) {
      console.error('[FORGOT PASSWORD] ========== ОШИБКА ==========');
      console.error('[FORGOT PASSWORD] Ошибка name:', e.name);
      console.error('[FORGOT PASSWORD] Ошибка message:', e.message);
      console.error('[FORGOT PASSWORD] Ошибка stack:', e.stack);
      
      let errorMessage = e.message || 'Произошла ошибка при отправке запроса';
      
      // Улучшенные сообщения об ошибках
      if (e.name === 'TypeError' && e.message.includes('fetch')) {
        errorMessage = 'Ошибка подключения к серверу. Проверьте подключение к интернету.';
      } else if (e.message.includes('Failed to fetch')) {
        errorMessage = 'Не удалось подключиться к серверу. Проверьте, что сервер запущен.';
      }
      
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const [telegramAuthEnabled, setTelegramAuthEnabled] = useState(false);
  const [telegramBotName, setTelegramBotName] = useState(null);

  // Проверка доступности Telegram авторизации
  useEffect(() => {
    const checkTelegramAuth = async () => {
      try {
        const response = await fetch('/api/public/telegram-auth-enabled');
        if (response.ok) {
          const data = await response.json();
          setTelegramAuthEnabled(data.enabled);
          setTelegramBotName(data.bot_name);
        }
      } catch (e) {
        console.error('Ошибка проверки Telegram авторизации:', e);
        setTelegramAuthEnabled(false);
      }
    };
    checkTelegramAuth();
  }, []);

  // Обработка авторизации через Telegram
  useEffect(() => {
    // Если Telegram авторизация отключена, не загружаем виджет
    if (!telegramAuthEnabled || !telegramBotName) {
      return;
    }
    
    // Загружаем скрипт Telegram Login Widget
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', telegramBotName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    
    // Глобальная функция для обработки авторизации
    window.onTelegramAuth = async (telegramUser) => {
      setTelegramLoading(true);
      setError(null);
      try {
        if (!telegramUser || !telegramUser.id) {
          throw new Error('Не удалось получить данные из Telegram. Проверьте настройки домена бота в @BotFather.');
        }
        
        // Выполняем вход через Telegram и ждем загрузки данных пользователя
        const { role } = await telegramLogin(telegramUser);
        
        // Даем задержку для синхронизации состояния React
        // Это гарантирует, что данные пользователя будут установлены в контексте
        // и компоненты успеют получить обновленное состояние
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Перенаправляем только после успешной загрузки
        // Используем window.location.href для полного перезапуска приложения
        // Это гарантирует, что все компоненты получат актуальные данные
        if (role === 'ADMIN') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/dashboard';
        }
      } catch (err) {
        let errorMessage = err.message || 'Ошибка входа через Telegram';
        
        // Улучшенное сообщение об ошибке для разных случаев
        if (errorMessage.includes('domain') || errorMessage.includes('invalid')) {
          errorMessage = 'Ошибка: Домен не настроен в @BotFather. Убедитесь, что домен добавлен в настройках бота.';
        } else if (errorMessage.includes('не найден в боте') || errorMessage.includes('not found in bot')) {
          errorMessage = 'Пользователь не найден в боте. Убедитесь, что вы зарегистрированы через Telegram бота.';
        } else if (errorMessage.includes('API бота') || errorMessage.includes('Bot API')) {
          errorMessage = `Ошибка подключения к API бота: ${errorMessage}. Проверьте настройки BOT_API_URL и BOT_API_TOKEN.`;
        } else if (errorMessage.includes('RemnaWave')) {
          errorMessage = 'Пользователь найден в боте, но не найден в RemnaWave. Обратитесь к администратору.';
        }
        
        setError(errorMessage);
        setTelegramLoading(false);
      }
    };
    
    // Обработка ошибок загрузки скрипта Telegram Widget
    script.onerror = () => {
      setError('Не удалось загрузить Telegram Login Widget. Проверьте подключение к интернету.');
    };
    
    const container = document.getElementById('telegram-login-container');
    if (container) {
      container.innerHTML = '';
      container.appendChild(script);
    }
    
    return () => {
      if (container) {
        container.innerHTML = '';
      }
      delete window.onTelegramAuth;
    };
  }, [telegramLogin, onLogin, telegramAuthEnabled, telegramBotName]);

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <div className="form-group">
        <label htmlFor="email">{t('auth.email')}</label>
        <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} placeholder="name@example.com" />
      </div>
      <div className="form-group">
        <label htmlFor="password">{t('auth.password')}</label>
        <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} placeholder="••••••••" />
        <button 
          type="button" 
          className="forgot-password-link" 
          onClick={() => {
            console.log('[FORGOT PASSWORD] Кнопка "Забыли пароль?" нажата');
            setError(null);
            setForgotPasswordEmail('');
            setForgotPasswordSuccess(false);
            setShowForgotPassword(true);
          }}
          style={{ 
            background: 'none', 
            border: 'none', 
            color: 'var(--accent-color)', 
            cursor: 'pointer', 
            fontSize: '14px', 
            marginTop: '8px',
            textDecoration: 'underline',
            padding: 0
          }}
        >
          {t('auth.forgot_password')}
        </button>
      </div>
      
      {error && !showForgotPassword && (
        <div className="message-error" style={{ marginBottom: '20px' }}>{error}</div>
      )}

      {notVerifiedError && (
        <div className="resend-container">
          <button type="button" className="btn-resend" disabled={resendStatus === 'sending'} onClick={handleResendVerification}>
            {resendStatus === 'sending' ? t('auth.resending') : t('auth.resend_button')}
          </button>
          {resendStatus === 'success' && <span className="resend-success">✅ {t('auth.resend_success')}</span>}
        </div>
      )}

      <button type="submit" className="btn btn-primary-glow" style={{ marginTop: '8px' }} disabled={loading || resendStatus === 'sending'}>
        {loading ? '...' : t('auth.login_button')}
      </button>
      
      {telegramAuthEnabled && (
        <>
          <div className="auth-divider">
            <span>{t('auth.or', 'или')}</span>
          </div>
          
          <div id="telegram-login-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1rem', gap: '0.5rem' }}>
            {telegramLoading && <div className="message-info">Авторизация через Telegram...</div>}
            {!telegramLoading && (
              <div className="telegram-login-hint" style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '0.5rem' }}>
                Если виджет не отображается, проверьте настройки домена в @BotFather
              </div>
            )}
          </div>
        </>
      )}

      {/* Модальное окно восстановления пароля */}
      {showForgotPassword && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => {
          if (!forgotPasswordLoading) {
            setShowForgotPassword(false);
            setForgotPasswordEmail('');
            setForgotPasswordSuccess(false);
            setError(null);
          }
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>{t('auth.forgot_password_title')}</h2>
            {error && showForgotPassword && (
              <div className="message-error" style={{ marginBottom: '20px' }}>
                {error}
              </div>
            )}
            {forgotPasswordSuccess ? (
              <div>
                <div className="message-success" style={{ marginBottom: '20px' }}>
                  {t('auth.forgot_password_success')}
                </div>
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordSuccess(false);
                  }}
                >
                  {t('auth.close')}
                </button>
              </div>
            ) : (
              <div>
                <div className="form-group">
                  <label htmlFor="forgot-email">{t('auth.email')}</label>
                  <input 
                    type="email" 
                    id="forgot-email" 
                    value={forgotPasswordEmail} 
                    onChange={(e) => {
                      console.log('[FORGOT PASSWORD] Email изменен:', e.target.value);
                      setForgotPasswordEmail(e.target.value);
                    }} 
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !forgotPasswordLoading && forgotPasswordEmail) {
                        e.preventDefault();
                        console.log('[FORGOT PASSWORD] Enter нажат, отправляем запрос');
                        handleForgotPassword(e);
                      }
                    }} 
                    required 
                    disabled={forgotPasswordLoading}
                    placeholder="name@example.com"
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    disabled={forgotPasswordLoading || !forgotPasswordEmail}
                    onClick={(e) => {
                      console.log('[FORGOT PASSWORD] Кнопка отправки нажата, email:', forgotPasswordEmail);
                      e.preventDefault();
                      e.stopPropagation();
                      if (forgotPasswordEmail) {
                        handleForgotPassword(e);
                      }
                    }}
                  >
                    {forgotPasswordLoading ? '...' : t('auth.send_password')}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setError(null);
                    }}
                    disabled={forgotPasswordLoading}
                  >
                    {t('auth.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

// --- Компонент Регистрации ---
const RegisterForm = ({ onRegister }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref'); 

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(null); setSuccess(null); setLoading(true);
    try {
      const data = await register(email, password, refCode);
      setSuccess(data.message); setEmail(''); setPassword('');
    } catch (err) { setError(err.message || t('auth.error_register')); } 
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {error && <div className="message-error">{error}</div>}
      {success && <div className="message-success">{success}</div>}
      
      {refCode && !success && (
        <div className="referral-badge">
          {t('auth.invited_by')} <strong>{refCode}</strong>
        </div>
      )}
      
      <div className="form-group">
        <label htmlFor="email">{t('auth.email')}</label>
        <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} placeholder="name@example.com" />
      </div>
      <div className="form-group">
        <label htmlFor="password">{t('auth.password')}</label>
        <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} placeholder="••••••••" />
      </div>
      
      <button type="submit" className="btn btn-primary-glow" disabled={loading}>
        {loading ? '...' : t('auth.register_button')}
      </button>
    </form>
  );
};


// --- Главная страница AuthPage (С НОВЫМ ДИЗАЙНОМ) ---
function AuthPage() {
  const { t } = useTranslation(); 
  const location = useLocation(); 
  const [isLoginView, setIsLoginView] = useState(location.pathname === '/login');
  const { branding, brandingLoading: loading } = useBranding();

  useEffect(() => {
    setIsLoginView(location.pathname === '/login');
  }, [location.pathname]);

  const handleAuthSuccess = (role) => {
    // Для Telegram логина используем полную перезагрузку страницы
    // чтобы гарантировать, что все компоненты получат актуальные данные
    if (role === 'ADMIN') {
      window.location.href = '/admin';
    } else {
      window.location.href = '/dashboard';
    }
  };

  if (loading || branding === null) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-card">
          <div className="loading-mini">Загрузка...</div>
        </div>
      </div>
    );
  }

  // Определяем текст приветствия
  const welcomeText = isLoginView 
    ? (branding.login_welcome_text || branding.site_subtitle || "Добро пожаловать в будущее безопасности.")
    : (branding.register_welcome_text || branding.site_subtitle || "Присоединяйтесь к невидимой сети.");

  // Разделяем название на части для стилизации
  const siteName = branding.site_name || '';
  const siteNameParts = siteName ? siteName.split(/(NET|VPN|VPN|NET)/i) : [];
  const displaySiteName = siteNameParts.length > 1 
    ? siteNameParts.map((part, i) => 
        /NET|VPN/i.test(part) ? <span key={i} className="text-accent">{part}</span> : part
      )
    : siteName;

  return (
    <div className="auth-page-wrapper">
      <div className="auth-card">
        
        {/* ❗️ ЛОГОТИП И ЗАГОЛОВОК */}
        <div className="auth-header">
          {branding.logo_url ? (
            <div className="auth-logo">
              <img src={branding.logo_url} alt={branding.site_name || ''} style={{ maxWidth: '200px', maxHeight: '60px' }} />
            </div>
          ) : (
            <div className="auth-logo">
              <LuShieldCheck />
            </div>
          )}
          <h1>{displaySiteName || ''}</h1>
          <p className="auth-subtitle">
            {welcomeText}
          </p>
        </div>

        {/* ПЕРЕКЛЮЧАТЕЛЬ */}
        <div className="auth-tabs">
          <Link to="/login" className={`auth-tab ${isLoginView ? 'active' : ''}`}>
            {t('auth.login')}
          </Link>
          <Link to="/register" state={location.state} search={location.search} className={`auth-tab ${!isLoginView ? 'active' : ''}`}>
            {t('auth.register')}
          </Link>
        </div>

        {isLoginView ? 
          <LoginForm onLogin={handleAuthSuccess} /> : 
          <RegisterForm onRegister={handleAuthSuccess} />
        }
        
        <div className="auth-footer">
          {branding.footer_text || ""}
        </div>
      </div>
    </div>
  );
}

export default AuthPage;